/**
 * Workshop showcase page for the travel frames (flight + calendar).
 *
 * Renders both frames with realistic mock data plus edge cases
 * (empty events, streaming, error). Calendar event clicks report
 * into a local log — they execute nothing.
 */

import { useState } from "react";
import { FlightFrame } from "@/workshop/components/shapes/flight-frame/flight-frame";
import { CalendarFrame } from "@/workshop/components/shapes/calendar-frame/calendar-frame";

function Section({
  title,
  children,
  testId,
}: {
  title: string;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <div className="mb-10" data-testid={testId}>
      <h2 className="mb-4 text-sm font-semibold text-white/80">{title}</h2>
      <div className="flex flex-wrap items-start gap-6">{children}</div>
    </div>
  );
}

export function TravelFramesPage() {
  const [lastEvent, setLastEvent] = useState<string | null>(null);

  return (
    <div className="workshop-canvas-bg min-h-full p-8" data-testid="travel-frames-page">
      <style>{`.workshop-canvas-bg{background-color:#0f1115;background-image:linear-gradient(0deg,transparent,transparent),linear-gradient(135deg,#2b2f36 0%,#343a42 40%,#3d424a 70%,#3b2f2a 100%);position:relative}.workshop-canvas-bg::before{content:"";position:absolute;inset:0;z-index:0;pointer-events:none;opacity:0.1;background:radial-gradient(circle at 22% 18%,rgba(156,132,255,0.18),transparent 58%),radial-gradient(circle at 72% 70%,rgba(111,168,198,0.12),transparent 60%)}.workshop-canvas-bg>*{position:relative;z-index:1}`}</style>
      <div className="mb-8">
        <h1 className="text-lg font-bold text-white">Travel Frames</h1>
        <p className="mt-1 text-sm text-white/40">
          Boarding-pass and week-planner frames. Click a calendar event
          to see it land in the log.
        </p>
        <div
          className="mt-3 flex min-h-8 flex-wrap items-center gap-2"
          data-testid="travel-frames-event-log"
          aria-live="polite"
        >
          {lastEvent ? (
            <span
              className="rounded-pill bg-white/[0.08] px-2 py-1 text-xs text-white/70"
              data-testid="travel-frames-last-event"
            >
              Event: {lastEvent}
            </span>
          ) : (
            <span className="text-xs text-white/30">
              No events clicked yet — click a calendar event.
            </span>
          )}
        </div>
      </div>

      <Section title="Flight — booked" testId="travel-frames-flight-done">
        <FlightFrame
          data-testid="travel-frames-flight"
        />
        <FlightFrame
          airline="United"
          flightNo="UA 901"
          from={{ code: "SFO", city: "San Francisco", time: "14:05" }}
          to={{ code: "LHR", city: "London", time: "08:10" }}
          duration="10h 05m"
          gate="G7"
          seat="22C"
          group="3"
          pnr="M4PL8Q"
          statusTag="Boarding"
          data-testid="travel-frames-flight-alt"
        />
      </Section>

      <Section title="Flight — edge cases" testId="travel-frames-flight-edge">
        <FlightFrame
          status="streaming"
          flightNo=""
          data-testid="travel-frames-flight-streaming"
        />
        <FlightFrame
          status="error"
          data-testid="travel-frames-flight-error"
        />
        <FlightFrame
          flightNo=""
          data-testid="travel-frames-flight-empty"
        />
      </Section>

      <Section title="Calendar — this week" testId="travel-frames-calendar-done">
        <CalendarFrame
          data-testid="travel-frames-calendar"
          onEventClick={setLastEvent}
        />
      </Section>

      <Section
        title="Calendar — edge cases"
        testId="travel-frames-calendar-edge"
      >
        <CalendarFrame
          events={[]}
          data-testid="travel-frames-calendar-empty"
        />
        <CalendarFrame
          status="streaming"
          data-testid="travel-frames-calendar-streaming"
        />
        <CalendarFrame
          status="error"
          data-testid="travel-frames-calendar-error"
        />
      </Section>
    </div>
  );
}
